import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { refreshToken as refreshExpiredToken } from "./refreshToken";
import { checkIfTokenIsExpired } from "./utils/jwt-extras";
import { getToken, setTokensToStorage, terminateTokens } from "./utils/localStorage";
import { getSSOAccessToken, msalInstance, SCOPES } from "../ssoConfig";
import { isFederatedUser } from "./helpers";

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

axiosInstance.defaults.headers.common["Cache-Control"] = "max-age=0";
axiosInstance.defaults.headers.common.Pragma = "no-cache";

axiosInstance.interceptors.request.use((config) => {
  if (config.url?.includes("data-export/at-home-devices")) {
    config.responseType = "arraybuffer";
  }

  const authToken = getToken("VERIS_ACCESS_TOKEN");
  const isTokenExpired = authToken ? checkIfTokenIsExpired(authToken) : true;
  if (config.headers && !isTokenExpired) config.headers.Authorization = `Bearer ${authToken}`;
  return config;
});

interface CustomAxiosRequest extends AxiosRequestConfig {
  retry?: boolean;
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest: CustomAxiosRequest = error.config as CustomAxiosRequest;
    if (shouldAttemptTokenRefresh(error, originalRequest)) {
      originalRequest.retry = true;
      const refreshToken = getToken("VERIS_REFRESH_TOKEN");

      if (!refreshToken && isFederatedUser()) {
        console.log("No refresh token available, trying msal");
        const account = msalInstance.getActiveAccount();
        if (account) {
          const token = await getSSOAccessToken(msalInstance, account, SCOPES);
          setTokensToStorage({ accessToken: token });
          originalRequest.headers!.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        }
      }
      if (!refreshToken) {
        logoutUser();
        return Promise.reject(error);
      }
      try {
        const newTokens = await refreshExpiredToken(refreshToken);
        setTokensToStorage(newTokens);
        originalRequest.headers!.Authorization = `Bearer ${newTokens.accessToken}`;
        return axiosInstance(originalRequest);
      } catch (err) {
        logoutUser();
        return Promise.reject(err);
      }
    } else if (shouldLogout(error)) {
      logoutUser();
      return Promise.reject(error);
    }
    return Promise.reject(error);
  },
);

function logoutUser() {
  terminateTokens();
  window.location.href = "/";
}

function shouldAttemptTokenRefresh(error: AxiosError, originalRequest: CustomAxiosRequest) {
  return (
    error.response?.status === 401 &&
    !!originalRequest.headers &&
    !originalRequest.retry &&
    !originalRequest.url?.includes("/login-staff") &&
    !originalRequest.url?.includes("/change-credential-staff") &&
    !originalRequest.url?.includes("/refresh")
  );
}

export function shouldLogout(error: AxiosError) {
  return (
    error.response?.status === 401 &&
    !error.config?.url?.includes("/login-staff") &&
    !error.config?.url?.includes("/change-credential-staff")
  );
}

export default axiosInstance;
